import type { ProjectStatus } from '../projects/types';
import type { Task } from '../tasks/types';

/**
 * Which status columns a section draws.
 *
 * Every section used to draw every column. A shipping section showed "In
 * Review" and "For Revision" whether or not anything in it could be in either,
 * and the columns that mattered were spread thin between ones that never
 * filled.
 *
 * section_statuses is an allow-list of columns to DRAW. It is deliberately not
 * a constraint on what status a task may hold — see the migration — and this
 * module is where that decision earns its keep.
 */

/** One section's configured column ids. Absent or empty means "draw them all". */
export type SectionStatusMap = Map<string, Set<string>>;

/**
 * The columns for the section currently in view.
 *
 * Three inputs and one non-obvious rule.
 *
 * The rule: a column holding tasks is ALWAYS drawn, configured or not. Without
 * it, narrowing a section's list would make its tasks vanish — not move, not
 * grey out, vanish — and the person who narrowed it would have no way to know
 * they had done it. That turns a layout choice into a destructive one, and
 * makes the safe-looking action the dangerous one.
 *
 * With it, narrowing is always reversible and never hides work: the column
 * stays as long as something is in it, and disappears on its own once the last
 * task moves out. Same principle as the CG pipeline board's catch-all column —
 * a row you cannot see is a row nobody fixes.
 *
 * `sectionId` is the tab: undefined means "All Tasks", where no single
 * section's preferences apply and every column is drawn.
 */
export function columnsForSection(
  statuses: ProjectStatus[],
  configured: SectionStatusMap,
  tasks: Task[],
  sectionId: string | null | undefined,
): ProjectStatus[] {
  // The All Tasks tab, or the "no section" tab. Neither is one section, so
  // neither has a list of its own; showing everything is the only honest
  // answer for a view spanning sections that disagree.
  if (typeof sectionId !== 'string' || sectionId === '') {
    return statuses;
  }

  const allowed = configured.get(sectionId);
  if (allowed === undefined || allowed.size === 0) {
    // Not configured. Distinct from "configured to show nothing", which this
    // deliberately cannot represent — see the migration's note on why empty
    // means everything.
    return statuses;
  }

  // Every status that actually holds a task in THIS section. Computed from the
  // tasks in hand rather than queried: the board already has them, and a
  // column that appears one render later than the task inside it is a flicker
  // people read as a bug.
  const occupied = new Set<string>();
  for (const task of tasks) {
    if (task.section_id === sectionId && task.status_id !== null) {
      occupied.add(task.status_id);
    }
  }

  return statuses.filter((status) => allowed.has(status.id) || occupied.has(status.id));
}

/**
 * Whether a column is being shown only because something is still in it.
 *
 * The board marks these, because the difference matters to the person looking:
 * an unconfigured column full of tasks is not part of this section's pipeline
 * and the tasks in it probably want moving. Silently drawing it identically
 * would hide the very thing the rule exists to surface.
 */
export function isOverflowColumn(
  statusId: string,
  configured: SectionStatusMap,
  sectionId: string | null | undefined,
): boolean {
  if (typeof sectionId !== 'string' || sectionId === '') {
    return false;
  }
  const allowed = configured.get(sectionId);
  if (allowed === undefined || allowed.size === 0) {
    return false;
  }
  return !allowed.has(statusId);
}

/**
 * Build the lookup the board reads, from the flat rows the query returns.
 *
 * Rows for sections with no configuration simply never appear, which is what
 * makes "absent" and "empty" the same thing here — and why the default costs
 * no storage and no backfill.
 */
export function toSectionStatusMap(
  rows: { section_id: string; status_id: string }[],
): SectionStatusMap {
  const map: SectionStatusMap = new Map();
  for (const row of rows) {
    const existing = map.get(row.section_id);
    if (existing === undefined) {
      map.set(row.section_id, new Set([row.status_id]));
    } else {
      existing.add(row.status_id);
    }
  }
  return map;
}

/**
 * The ids to tick in the configuration panel for a section.
 *
 * An unconfigured section reads as "all ticked" rather than "none ticked".
 * Showing empty checkboxes for a section that visibly draws every column would
 * be the panel contradicting the board, and the first change somebody made
 * from that state would look like it did the opposite of what they asked.
 */
export function configuredIdsFor(
  statuses: ProjectStatus[],
  configured: SectionStatusMap,
  sectionId: string,
): Set<string> {
  const allowed = configured.get(sectionId);
  if (allowed === undefined || allowed.size === 0) {
    return new Set(statuses.map((status) => status.id));
  }
  return new Set(allowed);
}

/**
 * What to send when somebody ticks every box.
 *
 * Ticking everything and clearing the list produce the same board, so the
 * stored form should be the one that keeps following the project: an explicit
 * copy of today's six statuses would NOT pick up a seventh added later, and
 * the section would quietly stop showing a column everybody else gained.
 */
export function statusesToSave(statuses: ProjectStatus[], chosen: Set<string>): string[] {
  if (chosen.size >= statuses.length && statuses.every((status) => chosen.has(status.id))) {
    return [];
  }
  return statuses.filter((status) => chosen.has(status.id)).map((status) => status.id);
}
